import { FC, useEffect } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import {
	createPpwrFeeRequest,
	resetSavePpwrFeeSuccess,
	updatePpwrFeeRequest,
} from 'redux/reducers/ppwr/reducer';
import { getPpwrFeeSaveLoading, getPpwrFeeSaveSuccess } from 'redux/reducers/ppwr/selectors';
import { TEprServiceFeeDialogProps, TEprServiceFeeFormValues } from './types';

const validationSchema = Yup.object({
	planName: Yup.string().required('Plan name is required').max(255, 'Max 255 characters'),
	monthlyFee: Yup.number().required('Monthly fee is required').min(0, 'Must be >= 0'),
	perOrderFee: Yup.number().required('Per order fee is required').min(0, 'Must be >= 0'),
});

export const EprServiceFeeDialog: FC<TEprServiceFeeDialogProps> = ({
	open,
	onClose,
	feeToEdit,
}) => {
	const dispatch = useDispatch();
	const isEdit = !!feeToEdit;

	const loading = useSelector(getPpwrFeeSaveLoading);
	const saveSuccess = useSelector(getPpwrFeeSaveSuccess);

	const formik = useFormik<TEprServiceFeeFormValues>({
		initialValues: {
			planName: feeToEdit?.planName ?? '',
			monthlyFee: feeToEdit?.monthlyFee ?? '',
			perOrderFee: feeToEdit?.perOrderFee ?? '',
		},
		enableReinitialize: true,
		validationSchema,
		onSubmit: (values) => {
			const body = {
				planName: values.planName,
				monthlyFee: Number(values.monthlyFee),
				perOrderFee: Number(values.perOrderFee),
			};
			if (isEdit && feeToEdit) {
				dispatch(updatePpwrFeeRequest({ id: feeToEdit.id, body }));
			} else {
				dispatch(createPpwrFeeRequest(body));
			}
		},
	});

	useEffect(() => {
		if (saveSuccess) {
			dispatch(resetSavePpwrFeeSuccess());
			formik.resetForm();
			onClose();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [saveSuccess]);

	const handleClose = () => {
		formik.resetForm();
		onClose();
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>{isEdit ? 'Edit EPR Service Fee' : 'Add New EPR Service Fee'}</DialogTitle>
			<DialogContent>
				<Box
					component="form"
					onSubmit={formik.handleSubmit}
					sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
				>
					<TextField
						label="Plan Name"
						name="planName"
						size="small"
						value={formik.values.planName}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.planName && !!formik.errors.planName}
						helperText={formik.touched.planName && formik.errors.planName}
					/>
					<TextField
						label="Monthly Fee"
						name="monthlyFee"
						type="number"
						size="small"
						inputProps={{ step: '0.01' }}
						value={formik.values.monthlyFee}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.monthlyFee && !!formik.errors.monthlyFee}
						helperText={formik.touched.monthlyFee && formik.errors.monthlyFee}
					/>
					<TextField
						label="Per Order Fee"
						name="perOrderFee"
						type="number"
						size="small"
						inputProps={{ step: '0.01' }}
						value={formik.values.perOrderFee}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.perOrderFee && !!formik.errors.perOrderFee}
						helperText={formik.touched.perOrderFee && formik.errors.perOrderFee}
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={() => formik.handleSubmit()}
					variant="contained"
					color="primary"
					disabled={loading}
					startIcon={loading ? <CircularProgress size={16} /> : undefined}
				>
					{isEdit ? 'Save' : 'Create'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
